import React from 'react';
import { executeNodeShellCommand } from '../../../../utils';
import { EuiPanel, EuiCodeBlock, EuiText, EuiProgress } from '@elastic/eui';
import { ErrorDisplay } from '../../../../components/ErrorDisplay';
import ElementErrorBoundary from '../../../../components/ErrorBoundary/ElementErrorBoundary';

export class Console extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      isError: false,
      output: '',
      secondaryError: false,
    };
    this.initShell()
      .then(() => {})
      .catch((e) => {
        console.log(e);
        this.setState({ isError: e.toString(), isLoading: false });
      });
  }

  // Transport failures (including a Piezo signing miss, which fails the operation before it is sent)
  // reject instead of returning a payload, and the constructor's catch renders them.
  handleResult = (executeCommand) => {
    if (!executeCommand?.success) {
      this.setState({
        // Guarded because JSON.stringify(undefined) returns the VALUE undefined, not a string, and
        // `isError: undefined` is falsy -- so a null payload rendered neither output nor an error.
        isError: executeCommand
          ? JSON.stringify(executeCommand, undefined, 4)
          : 'Request failed: no response from the server.',
        isLoading: false,
      });
      return;
    }
    // `success` says nothing about the shape of `data`, and a bad one would throw past the error
    // rendering above, leaving the panel blank.
    const data = executeCommand.data;
    if (!Array.isArray(data) || data.length < 2 || !data[0] || !data[1]) {
      this.setState({
        isError: `Unexpected response shape:\n${JSON.stringify(executeCommand, undefined, 4)}`,
        isLoading: false,
      });
      return;
    }
    const [{ stdout, stderr }, { status }] = data;
    this.setState({
      isLoading: false,
      output: `${stdout}${stdout && stderr ? '\n' : ''}${stderr}`,
      secondaryError: status !== 'ok',
    });
  };

  initShell = async () => {
    const { type, cmd, args, cluster, node } = this.props;
    if (typeof cluster !== 'string' || typeof node !== 'string') {
      this.setState({
        isError: 'Both cluster and node IP should be strings.',
        isLoading: false,
      });
    } else if (type === 'shell') {
      if (typeof cmd === 'string' && typeof args === 'string') {
        this.handleResult(
          await executeNodeShellCommand(
            'aes/domain/dp/node/shell',
            cluster,
            node,
            {
              base_cmd: cmd,
              args,
              truncate_output: false,
            }
          )
        );
      } else {
        this.setState({
          isError: 'Both command and arguments should be strings.',
          isLoading: false,
        });
      }
    } else {
      if (typeof cmd === 'string') {
        // `cmd` is a tool path here, and none of the sdpdagent tools declare truncate_output.
        this.handleResult(
          await executeNodeShellCommand(cmd, cluster, node, { ...args })
        );
      } else {
        this.setState({
          isError: 'The command should be a string.',
          isLoading: false,
        });
      }
    }
  };

  render() {
    const { props, state } = this;
    const { type, cmd, args } = props;
    const { isError, isLoading, output, secondaryError } = state;
    let content = null;
    if (isError) {
      content = <ErrorDisplay message={isError} />;
    } else if (!isLoading) {
      content = (
        <ElementErrorBoundary key={'code-block'}>
          <EuiCodeBlock
            language="shell"
            fontSize="m"
            paddingSize="m"
            overflowHeight="500"
            whiteSpace="pre-wrap"
            isCopyable={true}
          >
            {output}
          </EuiCodeBlock>
        </ElementErrorBoundary>
      );
    }

    return (
      <ElementErrorBoundary key={'single-console'}>
        <EuiPanel style={isLoading ? { position: 'relative' } : {}}>
          {isLoading ? (
            <EuiProgress size="xs" position="absolute" color="primary" />
          ) : null}
          <EuiText>
            <b>{'Type : '}</b>
            {type}
            <br />
            <b>{'Command : '}</b>
            {JSON.stringify(cmd)}
            <br />
            <b>{'Arguments : '}</b>
            {JSON.stringify(args, undefined, 3)}
          </EuiText>
          {secondaryError ? (
            <span style={{ color: 'red' }}>{'An error occured.'}</span>
          ) : null}
          <br />
          {content}
        </EuiPanel>
      </ElementErrorBoundary>
    );
  }
}
